import { useEffect, useRef, useState } from 'react';
import { bindParticlePointer } from '../lib/particlePointer';
import { isometricCubePaths } from '../lib/canvasGeometry';

const DURATION = 2600;
const ISO_X = .8660254;
const clamp = value => Math.max(0, Math.min(1, value));
const noise = n => { const v = Math.sin(n * 127.1 + 73.4) * 43758.5453; return v - Math.floor(v); };
const riverCentre = z => .6 + 2.5 * Math.sin((z + 3) * .35);
const isRiver = (x, z) => Math.abs(x - riverCentre(z)) < 1.7;
const elevation = (x, z) => {
  if (Math.abs(x) > 13 || Math.abs(z) > 11) return -1;
  if (isRiver(x, z)) return 0;
  const left = 12 - Math.hypot((x + 7) * 1.45, (z + 5) * 1.45);
  const right = 9 - Math.hypot((x - 6) * 1.65, (z + 6) * 1.65);
  return Math.max(1, Math.ceil(left), Math.ceil(right));
};
function makeNature() {
  const map = new Map();
  const add = (x, z, y, part, material) => map.set(`${x}:${z}:${y}`, { x, z, y, part, material, seed: noise(x * 17 + z * 31 + y * 11), px: (x - z) * ISO_X, py: (x + z) * .5 - y, dx: 0, dy: 0 });
  for (let x = -13; x <= 13; x++) for (let z = -11; z <= 11; z++) {
    const h = elevation(x, z), river = isRiver(x, z);
    // Riverbed is lower than the near-side meadow, not a stripe over a mountain.
    add(x, z, river ? -1 : 1, 0, river ? 'bed' : z >= 1 ? 'meadow' : 'ground');
    if (river) add(x, z, 0, 1, 'river');
    if (h > 1) {
      // Visible terrace sides only: no hidden volume that would waste frames.
      const low = Math.max(2, Math.min(elevation(x + 1, z), elevation(x, z + 1)) + 1);
      for (let y = low; y <= h; y++) add(x, z, y, 2, y >= 8 ? 'snow' : 'rock');
      add(x, z, h, 2, h >= 8 ? 'snow' : 'rock');
    }
    if (!river && (Math.abs(x) === 13 || z === 11)) add(x, z, 0, 0, 'ground');
    if (h === 1 && z >= 1 && noise(x * 43 + z * 5) > .79) add(x, z, 2, 3, 'tuft');
  }
  return [...map.values()].sort((a, b) => a.x + a.z - b.x - b.z || a.y - b.y);
}
const NATURE = makeNature();
const bounds = { left: Math.min(...NATURE.map(c => c.px)) - 2, right: Math.max(...NATURE.map(c => c.px)) + 2, top: Math.min(...NATURE.map(c => c.py)) - 2, bottom: Math.max(...NATURE.map(c => c.py)) + 2 };
const centre = { x: (bounds.left + bounds.right) / 2, y: (bounds.top + bounds.bottom) / 2 };

export const MedTourismAINatureCanvas = ({ reduced = false, active = false, label }) => {
  const ref = useRef(null), motion = useRef(reduced), replayRef = useRef(null), wakeRef = useRef(null);
  const [supported, setSupported] = useState(true);
  motion.current = reduced;
  useEffect(() => {
    const canvas = ref.current, stage = canvas.parentElement;
    let context;
    try { context = canvas.getContext('2d'); } catch { /* Static nature illustration fallback below. */ }
    if (!context) { setSupported(false); return; }
    const cells = NATURE.map(cell => ({ ...cell }));
    const style = getComputedStyle(stage), token = name => style.getPropertyValue(name).trim();
    const palette = { ink: token('--product-ink'), accent: token('--product-accent'), paper: token('--product-bg'), top: token('--product-cube-top'), left: token('--product-cube-left'), right: token('--product-cube-right') };
    const groundOpacity = Number(token('--med-ground-opacity')) || .4, meadowOpacity = Number(token('--med-meadow-opacity')) || .55, riverOpacity = Number(token('--med-river-opacity')) || .82, gap = Number(token('--med-cube-gap')) || .91;
    const riverCellCount = cells.filter(c => c.part === 1).length, meadowTuftCount = cells.filter(c => c.part === 3).length;
    const pointer = bindParticlePointer(canvas, { project: event => { const box = canvas.getBoundingClientRect(); return { x: event.clientX - box.left, y: event.clientY - box.top }; }, isReduced: () => motion.current });
    let width = 0, height = 0, unit = 1, dpr = 1, elapsed = 0, last = 0;
    let visible = false, started = false, disposed = false, raf = 0, resizeFrame = 0, replayCount = 0;
    let cubePaths;
    const polygon = (path, fill) => { context.fillStyle = fill; context.fill(path); };
    const drawCube = (x, y, cell, alpha) => {
      context.setTransform(dpr, 0, 0, dpr, x * dpr, y * dpr);
      const { left, right, top } = cubePaths;
      const snow = cell.material === 'snow', water = cell.material === 'river', meadow = cell.material === 'meadow', tuft = cell.material === 'tuft';
      context.globalAlpha = alpha * (cell.part === 0 ? meadow ? meadowOpacity : groundOpacity : water ? riverOpacity : 1);
      polygon(left, snow ? palette.paper : water || meadow || tuft ? palette.accent : palette.ink); polygon(left, palette.left);
      polygon(right, snow ? palette.accent : palette.ink); polygon(right, palette.right);
      polygon(top, snow || water && cell.seed > .9 ? palette.paper : palette.accent);
      if (!snow && !water) polygon(top, palette.top);
    };
    function draw(now, delta = 0) {
      if (!width || !height) return false;
      const reduce = motion.current;
      if (reduce) elapsed = DURATION;
      else if (started) elapsed = Math.min(DURATION, elapsed + delta);
      const progress = reduce ? 1 : elapsed / DURATION, contact = pointer.read(now), interactive = !reduce && contact.active;
      const radius = Math.min(width * .28, 105), blend = reduce ? 1 : 1 - Math.exp(-Math.max(delta, 16) * .012);
      let displacement = 0;
      context.setTransform(dpr, 0, 0, dpr, 0, 0); context.clearRect(0, 0, width, height);
      for (const cell of cells) {
        const delay = cell.part === 0 ? cell.seed * .06 : cell.part === 1 ? .14 + (cell.z + 11) / 22 * .12 : cell.part === 2 ? .30 + cell.y / 12 * .30 + cell.seed * .04 : .72 + cell.seed * .06;
        const local = reduce ? 1 : clamp((progress - delay) / (cell.part === 0 ? .23 : cell.part === 2 ? .27 : .22));
        if (!local) continue;
        const ease = 1 - (1 - local) ** 3;
        const bx = width / 2 + (cell.px - centre.x) * unit, by = height / 2 + (cell.py - centre.y) * unit;
        const vx = bx - contact.x, vy = by - contact.y, distance = interactive ? Math.hypot(vx, vy) || 1 : 1;
        const force = interactive ? Math.max(0, 1 - distance / radius) ** 2 * unit * 1.8 : 0;
        cell.dx += ((reduce ? 0 : vx / distance * force) - cell.dx) * blend;
        cell.dy += ((reduce ? 0 : vy / distance * force) - cell.dy) * blend;
        displacement = Math.max(displacement, Math.hypot(cell.dx, cell.dy));
        const flight = (1 - ease) * unit;
        drawCube(bx + (cell.seed - .5) * flight * 8 + cell.dx, by - flight * (6 + cell.seed * 8) + cell.dy, cell, local);
      }
      context.globalAlpha = 1;
      canvas.dataset.ready = 'true'; canvas.dataset.state = reduce ? 'static' : !started ? 'waiting' : progress < 1 ? 'assembling' : 'assembled';
      canvas.dataset.build = progress.toFixed(3); canvas.dataset.displacement = displacement.toFixed(2); canvas.dataset.particleCount = String(cells.length);
      canvas.dataset.peakCount = '2'; canvas.dataset.riverCells = String(riverCellCount); canvas.dataset.meadowTufts = String(meadowTuftCount); canvas.dataset.replayCount = String(replayCount);
      canvas.dataset.pointerActive = String(interactive); canvas.dataset.input = contact.input; canvas.dataset.contact = String(contact.contact); canvas.dataset.lastEnd = contact.lastEnd;
      return !reduce && (started && progress < 1 || interactive || displacement > .05);
    }
    function frame(now) {
      raf = 0;
      if (disposed || !visible || document.hidden) return;
      if (last && now - last < 32) { raf = requestAnimationFrame(frame); return; }
      const delta = Math.min(70, last ? now - last : 16); last = now;
      if (draw(now, delta)) raf = requestAnimationFrame(frame);
    }
    function wake() { if (!disposed && visible && !document.hidden && !raf) { last = 0; raf = requestAnimationFrame(frame); } }
    const resize = () => {
      if (disposed) return;
      width = stage.clientWidth; height = stage.clientHeight; dpr = Math.min(devicePixelRatio || 1, 1.5);
      canvas.width = Math.round(width * dpr); canvas.height = Math.round(height * dpr);
      unit = Math.min(width / (bounds.right - bounds.left + 5), height / (bounds.bottom - bounds.top + 5));
      cubePaths = isometricCubePaths(unit * gap, ISO_X);
      draw(performance.now()); wake();
    };
    const resizeObserver = new ResizeObserver(() => { cancelAnimationFrame(resizeFrame); resizeFrame = requestAnimationFrame(resize); });
    resizeObserver.observe(stage); resize();
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0]; visible = entry.isIntersecting;
      if (entry.intersectionRatio >= .4 && !started) { started = true; elapsed = 0; }
      if (visible) wake();
      else { cancelAnimationFrame(raf); raf = 0; last = 0; started = false; elapsed = 0; pointer.reset('offscreen'); }
    }, { threshold: [0, .4] });
    observer.observe(stage);
    const visibilityChange = () => { if (document.hidden) { cancelAnimationFrame(raf); raf = 0; } else wake(); };
    document.addEventListener('visibilitychange', visibilityChange);
    const events = ['pointermove', 'pointerdown', 'pointerup', 'pointerleave', 'pointercancel'];
    events.forEach(event => canvas.addEventListener(event, wake, { passive: true }));
    replayRef.current = () => {
      if (motion.current) return;
      elapsed = 0; started = true; replayCount++; pointer.reset('replay'); cells.forEach(cell => { cell.dx = 0; cell.dy = 0; }); draw(performance.now()); wake();
    };
    wakeRef.current = () => { draw(performance.now()); wake(); };
    return () => {
      disposed = true; cancelAnimationFrame(raf); cancelAnimationFrame(resizeFrame); observer.disconnect(); resizeObserver.disconnect(); pointer.dispose();
      events.forEach(event => canvas.removeEventListener(event, wake)); document.removeEventListener('visibilitychange', visibilityChange); replayRef.current = null; wakeRef.current = null;
    };
  }, []);
  useEffect(() => { wakeRef.current?.(); }, [reduced]);
  useEffect(() => { if (active && !reduced) replayRef.current?.(); }, [active, reduced]);
  return <span className="court-ai-scales med-tourism-ai-nature" data-testid="med-tourism-ai-nature-figure"><span className="court-ai-scales-stage">
    <canvas ref={ref} hidden={!supported} role="img" aria-label={label} data-testid="med-tourism-ai-tile-canvas" />
    {!supported && <svg className="court-ai-scales-fallback" viewBox={`${bounds.left - 2} ${bounds.top - 2} ${bounds.right - bounds.left + 4} ${bounds.bottom - bounds.top + 4}`} role="img" aria-label={label} data-testid="med-tourism-ai-nature-fallback">{NATURE.map((cell, i) => <g key={i} transform={`translate(${cell.px} ${cell.py})`} opacity={cell.part === 0 ? .45 : 1}><path d="M-.79,-.455 L0,0 L0,.91 L-.79,.455Z" fill={cell.material === 'snow' ? 'var(--product-bg)' : 'var(--product-accent)'} /><path d="M0,0 L.79,-.455 L.79,.455 L0,.91Z" fill="var(--product-ink)" /><path d="M0,-.91 L.79,-.455 L0,0 L-.79,-.455Z" fill={cell.material === 'snow' ? 'var(--product-bg)' : 'var(--product-accent)'} /></g>)}</svg>}
  </span></span>;
};
